import React, { useEffect, useState } from "react";
import { Tabs, Rate, Button, Input, List } from "antd";
import { HeartOutlined, ShoppingCartOutlined } from "@ant-design/icons";
import "react-responsive-carousel/lib/styles/carousel.min.css";
import { Carousel } from "react-responsive-carousel";
import { useSelector, useDispatch } from "react-redux";
import { addToWishlist } from "../function/users";
import { toast } from "react-toastify";
import _ from "lodash";
import { listPromotions } from "../function/promotion";
import { listRelatedProducts } from "../function/product";
import "./SingleProductCard.css";
import { addReview, getProductReviews } from "../function/review";
import ProductCard from "./ProductCard";
import LocationServiceCard from "./LocationServiceCard";
import LocationServiceChecker from "../page/LocationServiceChecker";
import axios from "axios";

const { TextArea } = Input;
const { TabPane } = Tabs;

const SingleProductCard = ({ product }) => {
  const dispatch = useDispatch();
  const { user } = useSelector((state) => ({ ...state }));
  const { _id, title, description, images, price, sold, quantity, category } =
    product;

  const isSoldOut = !quantity || quantity <= 0;
  const isInactive = product.isActive === false;
  const isUnavailable = isSoldOut || isInactive;

  const [discountedPrice, setDiscountedPrice] = useState(null);
  const [promotionDates, setPromotionDates] = useState({
    startDate: null,
    endDate: null,
  });
  const [promotionTitle, setPromotionTitle] = useState("");
  const [reviews, setReviews] = useState([]);
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState("");
  const [averageRating, setAverageRating] = useState(0);
  const [relatedProducts, setRelatedProducts] = useState([]);

  const [userLocation, setUserLocation] = useState(null);
  const [sortedServiceCenters, setSortedServiceCenters] = useState([]);
  const [serviceCenters, setServiceCenters] = useState([]);

  useEffect(() => {
    if (product?._id) fetchReviews(product._id);
  }, [product]);

  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition((position) => {
        setUserLocation({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
        });
      });
    }
  }, []);

  useEffect(() => {
    const fetchServiceCenters = async () => {
      if (product.serviceCenter && product.serviceCenter.length > 0) {
        const ids = product.serviceCenter.map((c) => c.$oid || c);
        const { data } = await axios.post(
          `${process.env.REACT_APP_API}/service-centers/by-ids`,
          { ids },
        );
        setServiceCenters(data);
      }
    };
    fetchServiceCenters();
  }, [product.serviceCenter]);

  function getDistanceFromLatLonInKm(lat1, lon1, lat2, lon2) {
    function deg2rad(deg) {
      return deg * (Math.PI / 180);
    }
    const R = 6371;
    const dLat = deg2rad(lat2 - lat1);
    const dLon = deg2rad(lon2 - lon1);
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(deg2rad(lat1)) *
        Math.cos(deg2rad(lat2)) *
        Math.sin(dLon / 2) *
        Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  }

  useEffect(() => {
    if (userLocation && serviceCenters.length > 0) {
      const sorted = [...serviceCenters]
        .map((center) => ({
          ...center,
          distance: getDistanceFromLatLonInKm(
            userLocation.latitude,
            userLocation.longitude,
            center.location.latitude,
            center.location.longitude,
          ),
        }))
        .sort((a, b) => a.distance - b.distance);
      setSortedServiceCenters(sorted);
    } else {
      setSortedServiceCenters(serviceCenters);
    }
  }, [userLocation, serviceCenters]);

  const fetchReviews = async (productId) => {
    try {
      const { data } = await axios.get(
        `${process.env.REACT_APP_API}/reviews/${productId}`,
      );
      setReviews(data.reviews);
      setAverageRating(parseFloat(data.averageRating));
    } catch (error) {
      console.error("Error fetching reviews:", error);
      toast.error("โหลดรีวิวไม่สำเร็จ");
    }
  };

  useEffect(() => {
    window.scrollTo(0, 0);

    const fetchPromotions = async () => {
      try {
        const res = await listPromotions();
        const promotions = res.data;
        const productPromotion = promotions.find((promotion) =>
          promotion.products.some((p) => p._id === product._id),
        );

        if (productPromotion) {
          setDiscountedPrice(product.discountedPrice);
          setPromotionDates({
            startDate: new Date(
              productPromotion.startDate,
            ).toLocaleDateString(),
            endDate: new Date(productPromotion.endDate).toLocaleDateString(),
          });
          setPromotionTitle(productPromotion.title);
        } else {
          setDiscountedPrice(null);
          setPromotionTitle("");
        }
      } catch (error) {
        console.error("Error fetching promotions:", error);
      }
    };

    fetchPromotions();
  }, [product]);

  useEffect(() => {
    if (!product) return;
    const categoryId =
      typeof product.category === "string"
        ? product.category
        : product.category?._id;
    
    const productCategoryId =
      typeof product.productCategory === "string"
        ? product.productCategory
        : product.productCategory?._id;
    if (categoryId && product._id) {
      fetchRelatedProducts(categoryId, product._id, productCategoryId);
    }
  }, [product]);

  const fetchRelatedProducts = async (
    categoryId,
    excludeProductId,
    productCategoryId,
  ) => {
    try {
      const { data } = await listRelatedProducts(
        categoryId,
        excludeProductId,
        productCategoryId,
      );
      setRelatedProducts(data);
    } catch (error) {
      console.error("Error fetching related products:", error);
    }
  };

  const handleAddToCart = () => {
    if (!product.isActive) {
      toast.error(
        `สินค้า ${product.title} ยังไม่พร้อมจำหน่าย จะกลับมาเร็วๆ นี้`,
      );
      return;
    }

    if (!product.quantity || product.quantity <= 0) {
      toast.error(`สินค้า ${product.title} หมดชั่วคราว`);
      return;
    }

    let cart = localStorage.getItem("cart")
      ? JSON.parse(localStorage.getItem("cart"))
      : [];

    const isEmployee = user && user.role === "employee";
    const discount = product?.employeeDiscount;
    let basePrice =
      product.discountedPrice !== undefined
        ? product.discountedPrice
        : product.price;

    const finalPrice =
      isEmployee && discount ? basePrice * (1 - discount / 100) : basePrice;

    cart.push({
      ...product,
      count: 1,
      originalPrice: product.price,
      price: finalPrice,
      discountedPrice: finalPrice,
    });

    let unique = _.uniqWith(cart, _.isEqual);
    localStorage.setItem("cart", JSON.stringify(unique));

    dispatch({ type: "ADD_TO_CART", payload: unique });
    dispatch({ type: "SET_VISIBLE", payload: true });
  };

  const handleAddToWishList = () => {
    if (user) {
      addToWishlist(user.token, _id)
        .then(() => toast.success("เพิ่มลงในรายการที่ชื่นชอบเรียบร้อย"))
        .catch((err) => console.log(err));
    } else {
      toast.error("กรุณาเข้าสู่ระบบก่อน");
    }
  };

  const handleSubmitReview = async () => {
    if (!rating || !comment) {
      toast.error("กรุณาให้คะแนนและเขียนรีวิว");
      return;
    }

    const reviewData = {
      user: user._id,
      product: product._id,
      rating,
      comment,
    };

    try {
      const res = await addReview(user.token, reviewData);
      toast.success("ส่งรีวิวสำเร็จ");
      setReviews([...reviews, res]);
    } catch (error) {
      toast.error("ส่งรีวิวไม่สำเร็จ");
    }
  };

  const formatTHB = (val) =>
    new Intl.NumberFormat("th-TH", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(val);

  const isEmployeeDiscount =
    user?.role === "employee" && product.employeeDiscount;

  return (
    <div className="spc-page">
      <div className="spc-container">
        {/* ===== Top: Image + Info ===== */}
        <div className="spc-top-grid">
          {/* Image carousel */}
          <div className="spc-image-card">
            <div className="spc-image-wrap">
              <Carousel
                autoPlay
                showArrows={true}
                infiniteLoop
                showThumbs={true}
              >
                {images &&
                  images.map((item) => (
                    <div
                      className="carousel-img-container"
                      key={item.public_id}
                    >
                      <img
                        src={item.url}
                        className="carousel-img"
                        alt={title}
                      />
                    </div>
                  ))}
              </Carousel>

              {isInactive ? (
                <div className="stock-badge inactive-badge">
                  ไม่พร้อมจำหน่าย
                </div>
              ) : isSoldOut ? (
                <div className="stock-badge sold-out-badge">SOLD OUT</div>
              ) : null}
            </div>
          </div>

          {/* Info / Price card */}
          <div className="spc-info-card">
            <h2 className="spc-title">
              {discountedPrice !== null ? promotionTitle : title}
            </h2>

            {discountedPrice !== null && (
              <span className="spc-promo-badge">🔥 โปรโมชั่นพิเศษ</span>
            )}

            <hr className="spc-divider" />

            {/* Price block */}
            <div className="spc-price-block">
              {isEmployeeDiscount ? (
                <>
                  <span className="spc-price-original">
                    {formatTHB(product.discountedPrice ?? price)}
                  </span>
                  <span className="spc-price-final">
                    {formatTHB(
                      (product.discountedPrice ?? price) *
                        (1 - product.employeeDiscount / 100),
                    )}{" "}
                    บาท
                  </span>
                  <span className="spc-price-badge">
                    ลด {product.employeeDiscount}% พนักงาน
                  </span>
                </>
              ) : discountedPrice !== null ? (
                <>
                  <span className="spc-price-original">{formatTHB(price)}</span>
                  <span className="spc-price-final">
                    {formatTHB(discountedPrice)} บาท
                  </span>
                </>
              ) : price !== null && price !== undefined ? (
                <span className="spc-price-final">{formatTHB(price)} บาท</span>
              ) : (
                <span className="spc-price-final">N/A</span>
              )}
            </div>

            <hr className="spc-divider" />

            {/* Info rows */}
            <div className="spc-info-rows">
              <div className="spc-info-row">
                <span>ขายแล้ว</span>
                <b>{sold}</b>
              </div>
              {product.category && (
                <div className="spc-info-row">
                  <span>หมวดหมู่</span>
                  <b>{category.name}</b>
                </div>
              )}
            </div>

            {/* Action buttons */}
            <div className="spc-action-row">
              <button
                className="spc-btn-wishlist"
                onClick={handleAddToWishList}
              >
                <HeartOutlined /> รายการโปรด
              </button>
              <button className="spc-btn-cart" onClick={handleAddToCart}>
                <ShoppingCartOutlined /> หยิบใส่ตะกร้า
              </button>
            </div>
          </div>
        </div>

        {/* ===== Tabs Section ===== */}
        <div className="spc-tabs-card">
          <Tabs type="card" className="spc-tabs">
            <TabPane tab="รายละเอียดสินค้า" key="1">
              <div
                className="spc-tab-content"
                dangerouslySetInnerHTML={{ __html: description }}
              />
            </TabPane>
            <TabPane tab="ข้อมูลเพิ่มเติม" key="2">
              <p className="spc-tab-content">ข้อมูลเพิ่มเติมเกี่ยวกับสินค้า</p>
            </TabPane>
            <TabPane tab="รีวิว" key="3">
              <div className="reviews-section">
                <div className="reviews-header">รีวิวจากผู้ใช้</div>

                <div className="spc-avg-rating-row">
                  <span>เรตติ้งเฉลี่ย</span>
                  <span>
                    <Rate disabled value={averageRating} />{" "}
                    <b>({averageRating})</b>
                  </span>
                </div>

                {reviews.length > 0 ? (
                  <List
                    dataSource={reviews}
                    renderItem={(review) => (
                      <List.Item
                        key={review._id}
                        style={{ border: "none", padding: 0 }}
                      >
                        <div className="review-item" style={{ width: "100%" }}>
                          <div className="review-author">
                            {review.user?.username || "Anonymous"}
                          </div>
                          <Rate
                            disabled
                            value={review.rating}
                            className="review-rating"
                          />
                          <div className="review-comment">{review.comment}</div>
                          <div className="review-time">
                            {new Date(review.createdAt).toLocaleString(
                              "th-TH",
                              {
                                day: "numeric",
                                month: "long",
                                year: "numeric",
                                hour: "2-digit",
                                minute: "2-digit",
                              },
                            )}
                          </div>
                        </div>
                      </List.Item>
                    )}
                  />
                ) : (
                  <div className="no-reviews">
                    ยังไม่มีรีวิว เป็นคนแรกที่รีวิวสินค้านี้!
                  </div>
                )}
              </div>
            </TabPane>
          </Tabs>
        </div>

        {/* ===== Service Center ===== */}
        <div className="spc-service-section">
          <LocationServiceChecker />
        </div>
        <div className="spc-service-section">
          <LocationServiceCard
            serviceCenters={sortedServiceCenters.slice(0, 3)}
          />
        </div>

        {/* ===== Add Review ===== */}
        <div className="add-review-section">
          <h4>เขียนรีวิว</h4>
          <Rate value={rating} onChange={(value) => setRating(value)} />
          <TextArea
            rows={4}
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder="เขียนความคิดเห็นของคุณที่นี่"
          />
          <button
            className="spc-btn-submit-review"
            onClick={handleSubmitReview}
          >
            ส่งรีวิว
          </button>
        </div>

        {/* ===== Related Products ===== */}
        <div className="related-products-section">
          <h4>สินค้าคล้ายกัน</h4>
          <div className="row g-2">
            {relatedProducts.length > 0 ? (
              relatedProducts.map((relatedProduct) => (
                <div className="col-6 col-md-3 mb-3" key={relatedProduct._id}>
                  <ProductCard product={relatedProduct} />
                </div>
              ))
            ) : (
              <p style={{ color: "#aaa", fontSize: 13 }}>ไม่มีสินค้าคล้ายกัน</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SingleProductCard;
